/*
 * pawlax-offer.js
 *
 * Page-native buybox for the PawLax offer test pages. One job: turn the colour
 * pickers plus the add-on toggle into a real /cart/add.js call and hand the
 * response to the theme's cart drawer, exactly the way the product page does.
 *
 * Money rules: this file never writes a price. The numbers on the page come from
 * Liquid (variant price x quantity) and every discount is the cart's business:
 * Kaching's bxgy function on the BOGO $29 product, the automatic discount on the
 * BOGO $20 product. What the drawer shows is what the checkout charges.
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function drawer() {
    var d = document.querySelector('cart-drawer');
    if (d && typeof d.renderContents === 'function') return Promise.resolve(d);
    if (!d) return Promise.resolve(null);
    if (!window.customElements) return Promise.resolve(null);
    return Promise.race([
      customElements.whenDefined('cart-drawer').then(function () {
        return typeof d.renderContents === 'function' ? d : null;
      }),
      new Promise(function (r) { setTimeout(function () { r(null); }, 1500); })
    ]);
  }

  ready(function () {
    var root = document.querySelector('[data-pox]');
    if (!root) return;

    var variants = {};
    try { variants = JSON.parse(root.getAttribute('data-variants') || '{}'); } catch (e) { variants = {}; }
    var qty = parseInt(root.getAttribute('data-qty') || '1', 10) || 1;
    var upsellId = root.getAttribute('data-upsell-id') || '';
    var sectionId = root.getAttribute('data-section-id') || '';

    var selects = Array.prototype.slice.call(root.querySelectorAll('[data-pox-select]'));
    var upsellBox = root.querySelector('[data-pox-upsell]');
    var errorEl = root.querySelector('[data-pox-error]');
    var buttons = [root.querySelector('[data-pox-atc]'), document.querySelector('[data-pox-atc-sticky]')].filter(Boolean);
    var sticky = document.querySelector('[data-pox-sticky]');

    /* ------------------------------------------------ items the click adds */
    function items() {
      var byId = {};
      var order = [];
      var count = 0;
      selects.forEach(function (s) {
        var v = variants[s.value];
        if (!v || !v.id) return;
        if (!byId[v.id]) { byId[v.id] = 0; order.push(v.id); }
        byId[v.id] += 1;
        count += 1;
      });
      // A picker count that does not match the offer quantity means the markup
      // was edited by hand; fill the gap with the first picker's choice rather
      // than silently adding fewer collars than the card promises.
      if (count < qty && order.length) { byId[order[0]] += (qty - count); }
      var list = order.map(function (id) { return { id: id, quantity: byId[id] }; });
      if (upsellBox && upsellBox.checked && upsellId) list.push({ id: parseInt(upsellId, 10), quantity: 1 });
      return list;
    }

    /* ------------------------------------------------ gallery follows collar 1 */
    function syncGallery() {
      var first = selects[0];
      if (!first) return;
      var v = variants[first.value];
      if (!v || !v.media) return;
      var gallery = document.querySelector('#MediaGallery-' + sectionId) || document.querySelector('media-gallery');
      if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
      try { gallery.setActiveMedia(sectionId + '-' + v.media, true); } catch (e) { /* gallery optional */ }
    }
    if (selects[0]) selects[0].addEventListener('change', syncGallery);

    /* ------------------------------------------------ add to cart */
    var busy = false;

    function setBusy(state) {
      busy = state;
      buttons.forEach(function (b) {
        b.classList.toggle('is-loading', state);
        b.setAttribute('aria-busy', state ? 'true' : 'false');
        b.disabled = state;
      });
    }

    function fail(msg) {
      if (errorEl) { errorEl.textContent = msg; errorEl.hidden = false; }
    }

    function add() {
      if (busy) return;
      if (errorEl) errorEl.hidden = true;
      var list = items();
      if (!list.length) { fail('Pick a colour first.'); return; }
      setBusy(true);
      drawer().then(function (d) {
        var payload = { items: list };
        if (d && typeof d.getSectionsToRender === 'function') {
          try {
            payload.sections = d.getSectionsToRender().map(function (s) { return s.id; });
            payload.sections_url = window.location.pathname;
          } catch (e) { /* drawer will refetch */ }
        }
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
          body: JSON.stringify(payload)
        }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
          .then(function (res) {
            if (!res.ok || res.body.status) {
              throw new Error(res.body.description || res.body.message || 'Could not add to cart');
            }
            if (d) {
              try { d.renderContents(res.body); return; } catch (e) { /* fall through */ }
            }
            window.location.href = '/cart';
          });
      }).catch(function (e) {
        fail(e && e.message ? e.message : 'Could not add to cart. Please try again.');
      }).then(function () { setBusy(false); });
    }

    buttons.forEach(function (b) { b.addEventListener('click', add); });

    /* ------------------------------------------------ sticky bar */
    if (sticky && 'IntersectionObserver' in window) {
      // A fixed bar inside a transformed ancestor is positioned against that
      // ancestor, not the viewport. Park it directly under body so it always
      // sits at the bottom of the screen.
      if (sticky.parentElement !== document.body) document.body.appendChild(sticky);
      var anchor = root.querySelector('[data-pox-atc]') || root;
      var show = function (on) {
        sticky.classList.toggle('is-visible', on);
        sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
      };
      // Visible only while the main button sits above the viewport: never when the
      // button is on screen, never before the reader has reached it. An
      // IntersectionObserver alone misses the jump from "below" to "above" (both
      // are "not intersecting"), so the scroll position is read on every scroll.
      var queued = false;
      function update() {
        queued = false;
        var r = anchor.getBoundingClientRect();
        show(r.bottom < 0);
      }
      function schedule() { if (!queued) { queued = true; requestAnimationFrame(update); } }
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      new IntersectionObserver(schedule, { threshold: 0 }).observe(anchor);
      update();
    }

    /* ------------------------------------------------ in-page anchors */
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('[data-pox-toreviews], [data-pox-tobuybox]');
      if (!a) return;
      var sel = a.hasAttribute('data-pox-tobuybox') ? '#pox-buybox' : a.getAttribute('href');
      var el = sel && sel.charAt(0) === '#' ? document.querySelector(sel) : null;
      if (!el) return;
      e.preventDefault();
      var hdr = document.querySelector('sticky-header, .header-wrapper');
      var pos = hdr ? getComputedStyle(hdr).position : '';
      var off = (pos === 'fixed' || pos === 'sticky') ? hdr.getBoundingClientRect().height : 0;
      var top = el.getBoundingClientRect().top + window.pageYOffset - off - 12;
      window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
    });
  });
})();
